import { RelatedProcessFlowType } from "@certa/studio-core/src/types/process.types";
import {
  type Operator,
  type RuleType
} from "@certa/studio-core/src/constants/rules.constants";

export const ENUM_TO_OPERATOR = {
  in: { id: "tasks.keyFieldFilterOperator.in", defaultMessage: "IN" },
  eq: { id: "tasks.keyFieldFilterOperator.eq", defaultMessage: "EQUALS" },
  nt: { id: "tasks.keyFieldFilterOperator.nt", defaultMessage: "NOT EQUAL" },
  gt: { id: "tasks.keyFieldFilterOperator.gt", defaultMessage: "GREATER THAN" },
  lt: { id: "tasks.keyFieldFilterOperator.lt", defaultMessage: "LESS THAN" },
  except: {
    id: "tasks.keyFieldFilterOperator.except",
    defaultMessage: "EXCEPT"
  },
  contains: {
    id: "tasks.keyFieldFilterOperator.contains",
    defaultMessage: "CONTAINS"
  }
};

export enum CompareType {
  "ANY" = "OR",
  "ALL" = "AND"
}

export enum ConstraintTypes {
  "VISIBILITY" = "constraints",
  "STEP_VISIBILITY" = "step__constraints",
  "DOCUMENT_INHERITANCE" = "attachment_inheritance_constraints", // Doesn't need condition
  "REDUCTION_CONSTRAINTS" = "reduction_constraints", // Doesn't need condition
  "STEP_ACTION_CONSTRAINTS" = "step__action_constraints",
  "STEP_ACTION_CONSTRAINTS_V2" = "step__action_constraints_v2",
  "INHERITANCE_CONSTRAINTS" = "inheritance_constraints_v2",
  "RELATED_PROCESS_INHERITANCE" = "related_workflow_field_constraints",
  "INTEGRATION_POLLING" = "integration_polling_constraints",
  "INTEGRATION_TRIGGER" = "integration_trigger_constraints",
  "STATUS_CONSTRAINTS" = "status_constraints",
  "STEP_ACCESS_GROUP_CONSTRAINTS" = "step__access_group_constraints",
  "STEP_GROUP_ACCESS_GROUP_CONSTRAINTS" = "stepgroup__access_group_constraints",
  "WORKFLOW_ACCESS_GROUP_CONSTRAINTS" = "workflow__access_group_constraints",
  "PDF_CONSTRAINTS" = "step__pdf_constraints",
  "NOTIFICATION_CONSTRAINTS" = "notifications"
}

export enum ReductionTypes {
  "SUM" = "sum",
  "AVERAGE" = "avg",
  "MAXIMUM" = "max",
  "MINIMUM" = "min",
  "CONCATENATE" = "concat",
  "PREPEND" = "prepend",
  "SPLIT" = "split",
  "APPEND" = "append",
  "WEIGHTED_AVERAGE" = "w_avg",
  "LENGTH" = "len",
  "SUBSTRING" = "substr",
  "DIFFERENCE" = "difference",
  "MULTIPLY" = "multiply",
  "DIVIDE" = "divide",
  "REPLACE" = "replace",
  "UPPERCASE" = "uppercase",
  "FORMULA" = "formula"
}

export const RelatedProcessParentKey = {
  [RelatedProcessFlowType.CHILD_TO_PARENT]: "target_workflow_kind",
  [RelatedProcessFlowType.PARENT_TO_CHILD]: "parent_workflow_kind"
};

// TODO: move rule, conditionGroups, conditions to central location
// https://thevetted.atlassian.net/browse/DS-3448
export type ConditionData = {
  id: string;
  firstCompareField: string;
  operator: Operator;
  __createdAt: number;
  __modifiedAt: number;
} & (
  | {
      // ensure that only one of the props is present, but not both
      secondCompareField: string;
      compareValue?: never;
    }
  | {
      secondCompareField?: never;
      compareValue: string;
    }
);

export type ConditionGroupData = {
  id: string;
  conditions: string[];
  groups: string[];
  compareType: CompareType;
  __createdAt: number;
  __modifiedAt: number;
};

/**
 * To hold all the conditions by their condition ID as key
 * and Condition data as value
 */
export type ConditionMap = Record<string, ConditionData>;

/**
 * To hold all the conditional groups, where group ID is the key
 * and the groupd details are the value
 */
export type ConditionGroupMap = Record<string, ConditionGroupData>;

export type RuleData = {
  id: string;
  conditions?: ConditionMap;
  groups?: ConditionGroupMap;
  actions: Record<string, any>;
  topLevelGroupId: string;
  name?: string;
  ruleType?: RuleType;
  ruleIndex?: number;
  isEnabled?: boolean;
  __targetFieldTag?: string;
  __newRule?: boolean;
  __modifiedAt: number;
  __createdAt: number;
};

export type RuleMap = Record<string, RuleData>;

export type Constraint = {
  first_compare_field?: string;
  second_compare_field?: string;
  compare_value?: string;
  operator?: string;
  compare_group: number;
} & Record<string, any>; // Record for actions
